import { useRef } from 'react';

import { PaidOutCashAggregations } from '~api/model/dashboardAggregations';

import { formatCurrency, message } from '~helper/_common';

import { useShowDetailsTile } from '~hooks/useShowDetailsTile';

import { LivestreamLabel } from '~components/molecules/LivestreamLabel';
import { ReplaysLabel } from '~components/molecules/ReplaysLabel';
import { CashDetailsTile } from '~components/organism/CashDetailsTile';
import { DataTile } from '~components/organism/DataTile';
import { DetailsTile } from '~components/organism/DetailsTile';
import { ComponentCommonProps } from '~components/types';

export type PaidOutCashDataTileProps = ComponentCommonProps & {
    loading: boolean;
    data: PaidOutCashAggregations['paidOutCash'] | null;
    creatorId?: string | null;
    hasDetailsTile?: boolean;
}

export const PaidOutCashDataTile: React.FC<PaidOutCashDataTileProps> = ({
    className,
    data,
    creatorId,
    loading,
    hasDetailsTile,
    'data-test': dataTest = 'paidOutCashDataTile',
}) => {
    const dataTileRef = useRef(null);
    const { showDetailsTile, setShowDetailsTile } = useShowDetailsTile();

    return (
        <>
            { showDetailsTile && (
                <DetailsTile
                    dataTileRef={ dataTileRef }
                    onClose={ () => setShowDetailsTile(false) }
                    data-test={ `${dataTest}.details` }
                >
                    <CashDetailsTile
                        data={ data }
                        creatorId={ creatorId }
                        mode="paidOutCash"
                        data-test={ `${dataTest}.details.cash` } />
                </DetailsTile>
            ) }
            <DataTile
                prominentMainDataValue
                className={ className }
                contentType="paidOutCash"
                headline={ message('dataTile.cash.headline') }
                headlineSecondary={ message('dataTile.cash.paidOut') }
                mainDataValue={ formatCurrency(data?.total || 0) }
                headlineVariant="h4"
                subHeadlineVariant="h5"
                loading={ loading }
                data-test={ dataTest }
                ref={ dataTileRef }
                onExpandClick={ hasDetailsTile ? () => setShowDetailsTile(true) : undefined }
            >
                <LivestreamLabel
                    loading={ loading }
                    data-test={ `${dataTest}.liveViews` }>
                    { formatCurrency(data?.liveViews || 0) }
                </LivestreamLabel>
                <ReplaysLabel
                    loading={ loading }
                    data-test={ `${dataTest}.replayViews` }>
                    { formatCurrency(data?.replayViews || 0) }
                </ReplaysLabel>
            </DataTile>
        </>
    );
};
